import { useRef, useCallback } from "react";

const AIRLINES = [
  { code: "AA", name: "American Airlines" },
  { code: "UA", name: "United Airlines" },
  { code: "DL", name: "Delta Air Lines" },
  { code: "WN", name: "Southwest Airlines" },
  { code: "EK", name: "Emirates" },
  { code: "AF", name: "Air France" },
  { code: "LH", name: "Lufthansa" },
  { code: "BA", name: "British Airways" },
  { code: "SQ", name: "Singapore Airlines" },
  { code: "QR", name: "Qatar Airways" },
  { code: "TK", name: "Turkish Airlines" },
  { code: "CX", name: "Cathay Pacific" },
  { code: "NH", name: "ANA" },
  { code: "KE", name: "Korean Air" },
  { code: "AC", name: "Air Canada" },
  { code: "KL", name: "KLM" },
  { code: "QF", name: "Qantas" },
  { code: "EY", name: "Etihad Airways" },
  { code: "MH", name: "Malaysia Airlines" },
  { code: "ET", name: "Ethiopian Airlines" },
];

const MAX_DIST = 160;
const MAX_SCALE = 2.0;
const NEIGHBOR_FALLOFF = 2.4;

export function AirlineLogosStrip() {
  const trackRef = useRef(null);
  const itemRefs = useRef([]);
  const doubled = [...AIRLINES, ...AIRLINES];

  const applyDock = useCallback((cursorX) => {
    itemRefs.current.forEach((el) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const dist = Math.abs(cursorX - cx);

      if (dist >= MAX_DIST) {
        el.style.transform = "scale(1) translateY(0px)";
        el.style.opacity = "0.7";
        el.style.zIndex = "0";
      } else {
        const ratio = 1 - dist / MAX_DIST;
        const scale = 1 + (MAX_SCALE - 1) * Math.pow(ratio, NEIGHBOR_FALLOFF);
        const lift = -14 * Math.pow(ratio, NEIGHBOR_FALLOFF);
        el.style.transform = `scale(${scale.toFixed(3)}) translateY(${lift.toFixed(1)}px)`;
        el.style.opacity = "1";
        el.style.zIndex = Math.round(ratio * 10).toString();
      }
    });
  }, []);

  const resetAll = useCallback(() => {
    if (trackRef.current) {
      trackRef.current.style.animationPlayState = "running";
    }
    itemRefs.current.forEach((el) => {
      if (!el) return;
      el.style.transform = "scale(1) translateY(0px)";
      el.style.opacity = "0.7";
      el.style.zIndex = "0";
    });
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (trackRef.current) {
      trackRef.current.style.animationPlayState = "paused";
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    applyDock(e.clientX);
  }, [applyDock]);

  return (
    <section
      className="bg-white border-y border-gray-100"
      style={{ paddingTop: "24px", paddingBottom: "32px" }}
      aria-label="Airlines we work with"
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={resetAll}
    >
      <div className="max-w-xl mx-auto text-center mb-5">
        <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">
          We search across all major airlines
        </p>
      </div>

      {/* Clip only horizontally so scaled logos aren't cropped vertically */}
      <div
        className="relative"
        style={{ overflowX: "clip", overflowY: "visible", paddingTop: "48px", paddingBottom: "8px" }}
      >
        {/* Left fade */}
        <div
          className="pointer-events-none absolute left-0 w-28 z-10"
          style={{ top: 0, bottom: 0, background: "linear-gradient(to right, #fff 0%, transparent 100%)" }}
        />
        {/* Right fade */}
        <div
          className="pointer-events-none absolute right-0 w-28 z-10"
          style={{ top: 0, bottom: 0, background: "linear-gradient(to left, #fff 0%, transparent 100%)" }}
        />

        <div ref={trackRef} className="marquee-track" style={{ alignItems: "flex-end" }}>
          {doubled.map((airline, i) => (
            <div
              key={`${airline.code}-${i}`}
              ref={(el) => { itemRefs.current[i] = el; }}
              title={airline.name}
              aria-label={airline.name}
              style={{
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-end",
                marginLeft: "28px",
                marginRight: "28px",
                transformOrigin: "bottom center",
                transition: "transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.18s ease",
                opacity: 0.7,
                cursor: "default",
                willChange: "transform",
              }}
            >
              <img
                src={`https://pics.avs.io/200/80/${airline.code}.png`}
                alt={`${airline.name} logo`}
                width={120}
                height={48}
                style={{ height: "40px", width: "auto", objectFit: "contain", display: "block" }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  const fb = e.currentTarget.nextSibling;
                  if (fb) fb.style.display = "flex";
                }}
              />
              {/* Fallback */}
              <span
                style={{ display: "none", fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em", color: "#9ca3af", textTransform: "uppercase" }}
              >
                {airline.code}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
